<template>

		<!-- if author profile should be shown and user has a public image -->
		<div v-if="useAuthorPublicProfile && user.publicImage" class="aspect-square bg-base-100 rounded-full flex-shrink-0 overflow-hidden">
			<img
				v-if="readyPublicImageUrl"
				:src="readyPublicImageUrl"
				class="rounded-full object-cover w-full h-full"
				loading="lazy"
				decoding="async"
				referrerpolicy="no-referrer"
			/>
			<span v-else class="font-semibold text-base-content/60 w-full h-full grid place-content-center" style="font-size: 40cqh">
				{{ user.getPublicNameInitials() ?? '' }}
			</span>
		</div>

		<!-- if author profile should be shown and user has NO public image -->
		<div v-else-if="useAuthorPublicProfile && !user.publicImage" class="aspect-square bg-base-100 rounded-full flex items-center justify-center flex-shrink-0" style="container-type: size;">
			<span class="font-semibold text-base-content/60" style="font-size: 40cqh">{{ user.getPublicNameInitials() ?? '' }}</span>
		</div>

		<!-- if author profile should NOT be shown and user has an avatar -->
		<div v-else-if="!useAuthorPublicProfile && user.avatar" class="aspect-square bg-base-100 rounded-full flex-shrink-0 overflow-hidden">
			<img
				v-if="readyAvatarUrl"
				:src="readyAvatarUrl"
				class="rounded-full object-cover w-full h-full"
				loading="lazy"
				decoding="async"
				referrerpolicy="no-referrer"
			/>
			<span v-else class="font-semibold text-base-content/60 w-full h-full grid place-content-center" style="font-size: 40cqh">
				{{ user.getInitials() }}
			</span>
		</div>

		<!-- if author profile should NOT be shown and user has NO avatar -->
		<div v-else-if="!useAuthorPublicProfile && !user.avatar" class="aspect-square bg-base-100 rounded-full flex items-center justify-center flex-shrink-0" style="container-type: size;">
			<span class="font-semibold text-base-content/60" style="font-size: 40cqh">{{ user.getInitials() }}</span>
		</div>

		<!-- if none of the above conditions are met -->
		<div v-else class="aspect-square bg-base rounded-full flex items-center justify-center flex-shrink-0">
			<UserCircleIcon class="w-full h-full text-base-300"/>
		</div>

</template>

<script lang="ts">
import { User } from "@/models/User.model";
import { UserCircleIcon } from "@heroicons/vue/24/solid";

// These module-level variables (by definition shared amongst all instances of UserAvatar) are used to ensure that each
// unique image URL is only loaded once, even if multiple parallel UserAvatar instances use the same URL. This optimization
// helps reduce redundant network requests, avoid "429 Too Many Requests" responses from external image servers (like Google's),
// and improve performance when the same avatar is used multiple time on the same page.
const imageLoadPromises = new Map<string, Promise<HTMLImageElement>>();
const loadedImages = new Map<string, HTMLImageElement>();
const renderableSrcByUrl = new Map<string, string>();

function ensureImageLoadedOnce(url: string): Promise<HTMLImageElement> {
	if (!url) {
		return Promise.reject(new Error('Missing image URL'));
	}

	if (loadedImages.has(url)) {
		return Promise.resolve(loadedImages.get(url)!);
	}

	const existingPromise = imageLoadPromises.get(url);
	if (existingPromise) {
		return existingPromise;
	}

	const promise = new Promise<HTMLImageElement>((resolve, reject) => {
		const img = new Image();
		img.decoding = 'async';
		img.crossOrigin = 'anonymous';
		img.referrerPolicy = 'no-referrer';
		img.onload = () => {
			loadedImages.set(url, img);
			imageLoadPromises.delete(url);
			resolve(img);
		};
		img.onerror = () => {
			imageLoadPromises.delete(url);
			reject(new Error(`Failed to load image: ${url}`));
		};
		img.src = url;
	});

	imageLoadPromises.set(url, promise);
	return promise;
}

// This function takes an image URL and its corresponding HTMLImageElement, and returns a renderable src string.
// The <img> tags can then use this renderable string rather than requesting the URL again, which would defeat
// the purpose of ensureImageLoadedOnce.
function getRenderableSrc(url: string, img: HTMLImageElement): string {
	const cachedRenderableSrc = renderableSrcByUrl.get(url);
	if (cachedRenderableSrc) {
		return cachedRenderableSrc;
	}

	try {
		const canvas = document.createElement('canvas');
		canvas.width = img.naturalWidth || img.width;
		canvas.height = img.naturalHeight || img.height;
		const context = canvas.getContext('2d');
		if (!context) {
			return img.src;
		}

		context.drawImage(img, 0, 0);
		const dataUrl = canvas.toDataURL('image/png');
		renderableSrcByUrl.set(url, dataUrl);
		return dataUrl;
	} catch {
		return img.src;
	}
}

export default {
	name: 'UserAvatar',

	props: {
		user: {
			type: User,
			required: true
		},
		usePublicProfile: {
			type: Boolean,
			default: false
		},
	},

	components: {
		UserCircleIcon
	},

	data() {
		return {
			readyPublicImageUrl: '',
			readyAvatarUrl: '',
		};
	},

	watch: {
		// Could possibly be done through a mounted() hook as well since props are likely to be immutable for the instance
		// lifetime, but just to be safe
		user: {
			immediate: true,
			deep: true,
			handler() {
				this.prepareImage();
			},
		},
		usePublicProfile() {
			this.prepareImage();
		},
	},

	computed: {
		useAuthorPublicProfile(): boolean {
			if(!this.usePublicProfile) {
				return false;
			}
			return this.user.publicName !== undefined || this.user.publicImage !== undefined;
		},

		publicImageUrl(): string {
			return (this.user.publicImage as any)?.url || '';
		},
	},

	methods: {
		async prepareImage() {
			this.readyPublicImageUrl = '';
			this.readyAvatarUrl = '';

			if (this.useAuthorPublicProfile && this.publicImageUrl) {
				try {
					const preloadedImage = await ensureImageLoadedOnce(this.publicImageUrl);
					this.readyPublicImageUrl = getRenderableSrc(this.publicImageUrl, preloadedImage);
				} catch {
					// keep fallback initials
				}
				return;
			}

			if (!this.useAuthorPublicProfile && this.user.avatar) {
				try {
					const preloadedImage = await ensureImageLoadedOnce(this.user.avatar);
					this.readyAvatarUrl = getRenderableSrc(this.user.avatar, preloadedImage);
				} catch {
					// keep fallback initials
				}
			}
		},
	}
}
</script>
